import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SmartphoneIcon, CloudUploadIcon, ImageIcon, ArrowLeftIcon, UploadCloudIcon, CheckCircle2Icon, SparklesIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Slider } from '../components/ui/Slider';
import { LogoIcon } from '../components/LogoIcon';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function InteriorAIPage() {
  const revealRef = useScrollReveal();
  const [roomType, setRoomType] = useState<string | string[]>('obyvacka');
  const [priceRange, setPriceRange] = useState<number | [number, number]>([500, 5000]);
  const [selectedColor, setSelectedColor] = useState('neutral');
  const roomOptions = [{
    value: 'obyvacka',
    label: 'Obývacia izba'
  }, {
    value: 'kuchyna',
    label: 'Kuchyňa'
  }, {
    value: 'spalna',
    label: 'Spálňa'
  }, {
    value: 'detska',
    label: 'Detská izba'
  }, {
    value: 'kupelna',
    label: 'Kúpeľňa a WC'
  }];
  const colors = [{
    id: 'neutral',
    class: 'bg-[#E5E5EA]',
    label: 'Neutrálna'
  }, {
    id: 'warm',
    class: 'bg-[#D4B59E]',
    label: 'Teplá'
  }, {
    id: 'cool',
    class: 'bg-[#A8C0D8]',
    label: 'Studená'
  }, {
    id: 'dark',
    class: 'bg-[#2C3E50]',
    label: 'Tmavá'
  }, {
    id: 'nature',
    class: 'bg-[#8F9779]',
    label: 'Prírodná'
  }];
  return <div className="min-h-screen bg-white text-apple-black font-sans selection:bg-apple-blue selection:text-white relative overflow-hidden" data-id="element-493">
      {/* Decorative background orbs */}
      <div className="gradient-orb w-[600px] h-[600px] bg-blue-300 top-[-100px] -right-[200px] fixed" data-id="element-494" />
      <div className="gradient-orb w-[500px] h-[500px] bg-purple-300 top-[600px] -left-[200px] fixed" data-id="element-495" />

      {/* Simplified Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100/80" data-id="element-496">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-497">
          <div className="flex justify-between items-center h-16" data-id="element-498">
            <Link to="/" className="flex items-center gap-2 text-apple-gray hover:text-apple-black transition-colors" data-id="element-499">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-500" />
              <span className="font-medium text-sm" data-id="element-501">
                Späť na hlavnú stránku
              </span>
            </Link>
            <div className="flex items-center gap-3" data-id="element-502">
              <LogoIcon size={32} data-id="element-503" />
              <span className="text-lg font-bold tracking-tight text-apple-black hidden sm:inline" data-id="element-504">
                Larsen Evans
              </span>
            </div>
            <div className="w-[150px] flex justify-end" data-id="element-505">
              <Button size="small" className="!bg-apple-black hover:!bg-gray-800 !text-white !rounded-full !px-4 !border-none" data-id="element-506">
                Prihlásiť sa
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-16" ref={revealRef} data-id="element-507">
        {/* Hero Section */}
        <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-508">
          <div className="reveal mb-12" data-id="element-509">
            <h1 className="text-[48px] font-bold tracking-tight text-apple-black mb-6 leading-tight" data-id="element-510">
              Interior AI
            </h1>
            <h2 className="text-[30px] font-medium text-apple-gray mb-6" data-id="element-511">
              Staň sa dizajnérom svojho priestoru
            </h2>
            <p className="text-[21px] text-apple-gray font-normal max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-512">
              Prinášame modernú AI aplikáciu, cez ktorú si vytvoríš vlastný
              dizajn interiéru svojho domu, bytu či iného priestoru.
            </p>
            <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold text-lg btn-hover-premium !border-none shadow-[0_0_30px_rgba(0,113,227,0.3)]" data-id="element-513">
              Chcem Pro verziu
            </Button>
          </div>

          <div className="reveal reveal-delay-1 max-w-5xl mx-auto" data-id="element-514">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-gray-50/50 backdrop-blur-sm" data-id="element-515">
              <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&h=600&fit=crop&crop=center" alt="Moderný interiér navrhnutý AI" className="w-full h-[400px] md:h-[600px] object-cover" data-id="element-516" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-517" />
              <div className="absolute bottom-8 left-8 flex gap-3" data-id="element-518">
                <span className="bg-white/90 backdrop-blur-md text-apple-black text-sm font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2" data-id="element-519">
                  <SparklesIcon className="w-4 h-4 text-apple-blue" data-id="element-520" />
                  Vygenerované za 3.2s
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Ako to funguje */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50/50" data-id="element-521">
          <div className="max-w-7xl mx-auto" data-id="element-522">
            <div className="reveal text-center mb-16" data-id="element-523">
              <h2 className="text-[36px] font-bold tracking-tight text-apple-black" data-id="element-524">
                Ako Interior AI funguje
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-525">
              {/* Connecting line for desktop */}
              <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-blue-100 via-purple-100 to-blue-100 z-0" data-id="element-526" />

              {/* Step 1 */}
              <div className="reveal reveal-delay-1 relative z-10 flex flex-col items-center text-center" data-id="element-527">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-528">
                  <SmartphoneIcon className="w-10 h-10 text-apple-blue" data-id="element-529" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-530">
                  Odfoť svoj priestor
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-531">
                  Vezmi svoj smartfón a urob fotku miestnosti, ktorú chceš
                  prerobiť. Nezáleží na tom, či je prázdna alebo plná starého
                  nábytku.
                </p>
              </div>

              {/* Step 2 */}
              <div className="reveal reveal-delay-2 relative z-10 flex flex-col items-center text-center" data-id="element-532">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-533">
                  <CloudUploadIcon className="w-10 h-10 text-purple-500" data-id="element-534" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-535">
                  Nahraj do aplikácie
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-536">
                  Nahraj fotku do nášho zabezpečeného cloudu. Vyber si typ
                  miestnosti, preferovaný štýl a farebnú paletu.
                </p>
              </div>

              {/* Step 3 */}
              <div className="reveal reveal-delay-3 relative z-10 flex flex-col items-center text-center" data-id="element-537">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-538">
                  <ImageIcon className="w-10 h-10 text-emerald-500" data-id="element-539" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-540">
                  Vygeneruj fotografiu
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-541">
                  Umelá inteligencia analyzuje priestor a za pár sekúnd ti
                  ponúkne fotorealistické návrhy tvojho nového vysnívaného
                  interiéru.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Upload & Settings Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-542">
          <div className="reveal bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden" data-id="element-543">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-544">
              {/* Left: Upload Area */}
              <div className="p-10 lg:p-16 bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col justify-center" data-id="element-545">
                <h3 className="text-2xl font-bold text-apple-black mb-6" data-id="element-546">
                  Nahraj fotografiu
                </h3>
                <div className="border-2 border-dashed border-gray-300 rounded-2xl bg-white p-12 flex flex-col items-center justify-center text-center cursor-pointer hover:border-apple-blue hover:bg-blue-50/30 transition-colors group" data-id="element-547">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-548">
                    <UploadCloudIcon className="w-10 h-10 text-apple-blue" data-id="element-549" />
                  </div>
                  <p className="text-lg font-medium text-apple-black mb-2" data-id="element-550">
                    Potiahni fotku sem
                  </p>
                  <p className="text-sm text-apple-gray mb-6" data-id="element-551">
                    alebo klikni pre výber súboru (JPG, PNG, max 10MB)
                  </p>
                  <Button variant="secondary" className="!rounded-full" data-id="element-552">
                    Vybrať súbor
                  </Button>
                </div>
              </div>

              {/* Right: Settings */}
              <div className="p-10 lg:p-16 flex flex-col justify-center" data-id="element-553">
                <h3 className="text-2xl font-bold text-apple-black mb-8" data-id="element-554">
                  Nastavenia dizajnu
                </h3>

                <div className="space-y-8" data-id="element-555">
                  {/* Room Type */}
                  <div data-id="element-556">
                    <label className="block text-sm font-semibold text-apple-black mb-3" data-id="element-557">
                      Typ priestoru
                    </label>
                    <Select options={roomOptions} value={roomType} onChange={setRoomType} placeholder="Vyberte typ miestnosti" data-id="element-558" />
                  </div>

                  {/* Color Palette */}
                  <div data-id="element-559">
                    <label className="block text-sm font-semibold text-apple-black mb-3" data-id="element-560">
                      Farebná paleta
                    </label>
                    <div className="flex flex-wrap gap-4" data-id="element-561">
                      {colors.map(color => <button key={color.id} onClick={() => setSelectedColor(color.id)} className={`flex flex-col items-center gap-2 focus:outline-none group`} data-id="element-562">
                          <div className={`w-12 h-12 rounded-full ${color.class} shadow-sm border-2 transition-all ${selectedColor === color.id ? 'border-apple-blue scale-110 ring-4 ring-blue-100' : 'border-transparent group-hover:scale-105'}`} data-id="element-563" />
                          <span className={`text-xs font-medium ${selectedColor === color.id ? 'text-apple-blue' : 'text-apple-gray'}`} data-id="element-564">
                            {color.label}
                          </span>
                        </button>)}
                    </div>
                  </div>

                  {/* Budget Slider */}
                  <div data-id="element-565">
                    <div className="flex justify-between items-center mb-3" data-id="element-566">
                      <label className="block text-sm font-semibold text-apple-black" data-id="element-567">
                        Odhadovaný rozpočet na realizáciu
                      </label>
                      <span className="text-sm font-bold text-apple-blue" data-id="element-568">
                        {Array.isArray(priceRange) ? `€${priceRange[0]} - €${priceRange[1]}` : `€${priceRange}`}
                      </span>
                    </div>
                    <div className="px-2" data-id="element-569">
                      <Slider min={500} max={20000} step={500} value={priceRange} onChange={setPriceRange} showLabels={false} data-id="element-570" />
                    </div>
                    <div className="flex justify-between text-xs text-apple-gray mt-2" data-id="element-571">
                      <span data-id="element-572">€500</span>
                      <span data-id="element-573">€20,000+</span>
                    </div>
                  </div>

                  <Button size="large" className="w-full !bg-apple-black hover:!bg-gray-800 !text-white !rounded-xl !py-4 !font-bold mt-4 shadow-lg" data-id="element-574">
                    Vygenerovať dizajn
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Form */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-center" data-id="element-575">
          <div className="reveal" data-id="element-576">
            <h2 className="text-3xl md:text-4xl font-bold text-apple-black mb-4" data-id="element-577">
              Pripravený na zmenu?
            </h2>
            <p className="text-lg text-apple-gray mb-10" data-id="element-578">
              Zanechaj nám kontakt a získaj prednostný prístup k Pro verzii s
              50% zľavou.
            </p>

            <form className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 text-left space-y-6" data-id="element-579">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-580">
                <Input label="Meno a priezvisko" placeholder="Jozef Mrkvička" data-id="element-581" />
                <Input label="E-mail" type="email" placeholder="jozef@priklad.sk" data-id="element-582" />
              </div>
              <Input label="Telefón (voliteľné)" type="tel" placeholder="+421 900 000 000" data-id="element-583" />
              <Button size="large" className="w-full !bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-xl !py-4 !font-bold" data-id="element-584">
                Získať prednostný prístup
              </Button>
              <p className="text-xs text-center text-apple-gray mt-4" data-id="element-585">
                Odoslaním súhlasíte so spracovaním osobných údajov.
              </p>
            </form>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-apple-light to-gray-100 pt-16 pb-8 px-4 sm:px-6 lg:px-8 mt-12" data-id="element-586">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent" data-id="element-587" />
        <div className="max-w-7xl mx-auto" data-id="element-588">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8" data-id="element-589">
            <div className="flex items-center gap-3" data-id="element-590">
              <LogoIcon size={32} data-id="element-591" />
              <span className="text-sm font-bold text-apple-black tracking-tight" data-id="element-592">
                Larsen Evans Interior AI
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-apple-gray" data-id="element-593">
              <Link to="/" className="hover:text-apple-black transition-colors duration-300" data-id="element-594">
                Hlavná stránka
              </Link>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-595">
                Cenník
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-596">
                Galéria
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-597">
                Kontakt
              </a>
            </div>
          </div>
          <div className="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-8" data-id="element-598" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-apple-gray" data-id="element-599">
            <p data-id="element-600">
              Copyright © 2026 Larsen Evans Enterprise Inc. Všetky práva
              vyhradené.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2" data-id="element-601">
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-602">
                Ochrana Súkromia
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-603">
                Podmienky Používania
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}